// Utility functions for localStorage operations

const AUTH_TOKEN_KEY = 'authToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_DATA_KEY = 'userData';

export const storage = {
  // Token management
  setToken: (token: string): void => {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  },

  getToken: (): string | null => {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  },

  removeToken: (): void => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  },

  // Refresh token management
  setRefreshToken: (token: string): void => {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  },

  getRefreshToken: (): string | null => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  removeRefreshToken: (): void => {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },

  // User data management
  setUserData: (userData: object): void => {
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
  },

  getUserData: <T = Record<string, unknown>>(): T | null => {
    const data = localStorage.getItem(USER_DATA_KEY);
    return data ? JSON.parse(data) : null;
  },

  removeUserData: (): void => {
    localStorage.removeItem(USER_DATA_KEY);
  },

  // Clear all auth-related data
  clearAuthData: (): void => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_DATA_KEY);
  },

  // Check if user is authenticated (has valid token and user data)
  isAuthenticated: (): boolean => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const userData = localStorage.getItem(USER_DATA_KEY);
    return !!(token && userData);
  },

  // Get auth state from storage
  getAuthState: () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const userData = localStorage.getItem(USER_DATA_KEY);
    return {
      token,
      user: userData ? JSON.parse(userData) : null,
    };
  },

  // Generic localStorage operations
  setItem: (key: string, value: string): void => {
    localStorage.setItem(key, value);
  },

  getItem: (key: string): string | null => {
    return localStorage.getItem(key);
  },

  removeItem: (key: string): void => {
    localStorage.removeItem(key);
  },

  clear: (): void => {
    localStorage.clear();
  },
};

// Factory function to create storage with custom keys
export const createStorage = (
  tokenKey: string = AUTH_TOKEN_KEY,
  userDataKey: string = USER_DATA_KEY
) => {
  return {
    setToken: (token: string): void => {
      localStorage.setItem(tokenKey, token);
    },

    getToken: (): string | null => {
      return localStorage.getItem(tokenKey);
    },

    removeToken: (): void => {
      localStorage.removeItem(tokenKey);
    },

    setUserData: (userData: object): void => {
      localStorage.setItem(userDataKey, JSON.stringify(userData));
    },

    getUserData: <T = Record<string, unknown>>(): T | null => {
      const data = localStorage.getItem(userDataKey);
      return data ? JSON.parse(data) : null;
    },

    removeUserData: (): void => {
      localStorage.removeItem(userDataKey);
    },

    clearAuthData: (): void => {
      localStorage.removeItem(tokenKey);
      localStorage.removeItem(userDataKey);
    },

    isAuthenticated: (): boolean => {
      const token = localStorage.getItem(tokenKey);
      const userData = localStorage.getItem(userDataKey);
      return !!(token && userData);
    },

    getAuthState: () => {
      const token = localStorage.getItem(tokenKey);
      const userData = localStorage.getItem(userDataKey);
      return {
        token,
        user: userData ? JSON.parse(userData) : null,
      };
    },
  };
};
